/**
 * Bug Explorer core module.
 * Shared runtime helpers (config, money, motion, focus trap, scroll lock) plus the
 * page-wide motion system: staged reveals, parallax, scroll scrubs and magnetic CTAs.
 * Imported by every other `be-*` module through the `@be/core` import-map entry.
 */
import { formatMoney } from '@theme/money-formatting';

const configEl = document.getElementById('be-config');

/** @type {{motion:number, moneyFormat:string, currency:string, routes:Record<string,string>, cartSection:string, strings:Record<string,string>}} */
export const config = configEl ? JSON.parse(configEl.textContent || '{}') : { routes: {}, strings: {} };

const reducedQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

/** 0 = no motion, 0.45 = subtle, 1 = full. Always 0 when the visitor prefers reduced motion. */
export function motionFactor() {
  if (reducedQuery.matches) return 0;
  return typeof config.motion === 'number' ? config.motion : 1;
}

/** @param {number} cents */
export function money(cents) {
  return formatMoney(Math.round(cents), config.moneyFormat || '{{amount}}', config.currency || 'EUR');
}

export const isDesignMode = () => Boolean(window.Shopify && window.Shopify.designMode);

/** Scroll position of the active scroll container (Helio scrolls `.page-wrapper` on desktop). */
export function scrollTop() {
  const wrapper = document.querySelector('.page-wrapper');
  return Math.max(window.scrollY || 0, wrapper ? wrapper.scrollTop : 0);
}

/** Locks page scroll using the theme's existing `scroll-lock` contract. */
const lockOwners = new Set();
export function lockScroll(owner, locked) {
  if (locked) lockOwners.add(owner);
  else lockOwners.delete(owner);
  document.documentElement.toggleAttribute('scroll-lock', lockOwners.size > 0);
  document.body.style.overflow = lockOwners.size > 0 ? 'hidden' : '';
}

const FOCUSABLE =
  'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

/**
 * Traps keyboard focus inside `container` and wires Escape.
 * @param {HTMLElement} container
 * @param {{ onEscape?: () => void, initialFocus?: HTMLElement | null }} [options]
 * @returns {() => void} release function (restores focus to the previously focused element)
 */
export function trapFocus(container, options = {}) {
  const previous = /** @type {HTMLElement|null} */ (document.activeElement);
  const focusables = () =>
    Array.from(container.querySelectorAll(FOCUSABLE)).filter(
      (el) => el instanceof HTMLElement && el.offsetParent !== null && !el.closest('[inert]')
    );
  /** @param {KeyboardEvent} event */
  const onKey = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      options.onEscape?.();
      return;
    }
    if (event.key !== 'Tab') return;
    const items = focusables();
    if (!items.length) {
      event.preventDefault();
      container.focus();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === container)) {
      event.preventDefault();
      /** @type {HTMLElement} */ (last).focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      /** @type {HTMLElement} */ (first).focus();
    }
  };
  document.addEventListener('keydown', onKey);
  requestAnimationFrame(() => {
    const target = options.initialFocus || focusables()[0] || container;
    target.focus({ preventScroll: true });
  });
  return () => {
    document.removeEventListener('keydown', onKey);
    if (previous && document.contains(previous)) previous.focus({ preventScroll: true });
  };
}

/** Shared POST helper for JSON endpoints. */
export async function postJSON(url, body, signal) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.status) {
    const error = new Error(data.description || data.message || config.strings.error || 'Error');
    // @ts-ignore
    error.status = response.status;
    throw error;
  }
  return data;
}

export const clamp = (value, min = 0, max = 1) => Math.max(min, Math.min(max, value));

/* ==========================================================================
   Motion system
   ========================================================================== */

const revealObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            entry.target.classList.add('is-in');
            revealObserver?.unobserve(entry.target);
          }
        },
        { rootMargin: '0px 0px -6% 0px', threshold: 0.06 }
      )
    : null;

/** Elements currently near the viewport that react to scroll (parallax / scrubs). */
const activeScroll = new Set();
const scrollObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) activeScroll.add(entry.target);
            else activeScroll.delete(entry.target);
          }
          requestFrame();
        },
        { rootMargin: '200px 0px 200px 0px' }
      )
    : null;

/**
 * Registers motion elements inside `root`. Idempotent; safe to call after section re-renders.
 * @param {ParentNode} [root]
 */
export function scanMotion(root = document) {
  root.querySelectorAll('[data-be-reveal]:not([data-be-rv])').forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    el.dataset.beRv = '1';
    const delay = Number(el.dataset.beDelay || 0);
    if (delay) el.style.setProperty('--be-delay', `${delay}ms`);
    if (!revealObserver || motionFactor() === 0) {
      el.classList.add('is-in');
      return;
    }
    revealObserver.observe(el);
  });

  root.querySelectorAll('[data-be-px], [data-be-scrub]').forEach((el) => {
    if (!(el instanceof HTMLElement) || el.dataset.beSo) return;
    el.dataset.beSo = '1';
    scrollObserver?.observe(el.parentElement || el);
    // map parent -> element so the observer entry can find it
    const host = el.parentElement || el;
    /** @type {any} */ (host).__beMotion = /** @type {any} */ (host).__beMotion || [];
    /** @type {any} */ (host).__beMotion.push(el);
  });

  if (motionFactor() > 0 && window.matchMedia('(pointer: fine)').matches) {
    root.querySelectorAll('[data-be-magnetic]:not([data-be-mg])').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      el.dataset.beMg = '1';
      el.addEventListener('pointermove', (event) => {
        const r = el.getBoundingClientRect();
        const f = motionFactor();
        el.style.transform = `translate(${(event.clientX - r.left - r.width / 2) * 0.18 * f}px, ${
          (event.clientY - r.top - r.height / 2) * 0.28 * f
        }px)`;
        el.style.transitionDuration = '.2s';
      });
      el.addEventListener('pointerleave', () => {
        el.style.transform = '';
        el.style.transitionDuration = '';
      });
    });
  }
  requestFrame();
}

let frameQueued = false;
function requestFrame() {
  if (frameQueued) return;
  frameQueued = true;
  requestAnimationFrame(() => {
    frameQueued = false;
    renderScroll();
  });
}

/** Named scroll effects; `q` is progress 0..1, `f` the motion factor. */
const scrubs = {
  /** Hero copy drifts up and fades as the page scrolls. */
  heroText(el, rect, vh, f) {
    const q = clamp(scrollTop() / vh);
    el.style.transform = `translate3d(0, ${(-q * 90 * f).toFixed(1)}px, 0)`;
    el.style.opacity = String(1 - q * 0.85 * f);
  },
  /** Hero product sinks and grows slightly. */
  heroMedia(el, rect, vh, f) {
    const q = clamp(scrollTop() / vh);
    el.style.transform = `translate3d(0, ${(q * 50 * f).toFixed(1)}px, 0) scale(${1 + q * 0.07 * f})`;
  },
  /** Image opens from an inset clip as it scrolls in. */
  clip(el, rect, vh, f) {
    const q = clamp((vh - rect.top) / (vh * 0.85));
    const inset = ((1 - q) * 12 * f).toFixed(2);
    el.style.clipPath = `inset(${inset}% ${inset}% ${inset}% ${inset}% round var(--be-radius-lg))`;
  },
  /** Product swings into place. */
  enter(el, rect, vh, f) {
    const q = clamp((vh - rect.top) / (vh * 0.9));
    el.style.transform = `translate3d(${((1 - q) * -70 * f).toFixed(1)}px, ${((1 - q) * 50 * f).toFixed(1)}px, 0) rotate(${(
      (1 - q) * -7 * f
    ).toFixed(2)}deg) scale(${(1 - (1 - q) * 0.14 * f).toFixed(3)})`;
    el.style.opacity = String(1 - (1 - q) * 0.85 * f);
  },
};

function renderScroll() {
  const f = motionFactor();
  const vh = window.innerHeight;
  for (const host of activeScroll) {
    if (!(host instanceof HTMLElement) || !host.isConnected) {
      activeScroll.delete(host);
      continue;
    }
    const rect = host.getBoundingClientRect();
    for (const el of /** @type {any} */ (host).__beMotion || []) {
      if (!el.isConnected) continue;
      if (!f) {
        el.style.transform = '';
        el.style.opacity = '';
        el.style.clipPath = '';
        continue;
      }
      if (el.dataset.bePx) {
        const center = (rect.top + rect.height / 2 - vh / 2) / vh;
        el.style.transform = `translate3d(0, ${(center * parseFloat(el.dataset.bePx) * 160 * f).toFixed(1)}px, 0)`;
      } else {
        const fn = scrubs[/** @type {keyof typeof scrubs} */ (el.dataset.beScrub)];
        if (fn) fn(el, rect, vh, f);
      }
    }
  }
  document.dispatchEvent(new CustomEvent('be:scroll'));
}

// One capture listener catches window and `.page-wrapper` scrolling alike.
document.addEventListener('scroll', requestFrame, { passive: true, capture: true });
window.addEventListener('resize', requestFrame, { passive: true });

/** Subscribe to the shared scroll frame. Returns an unsubscribe function. */
export function onScrollFrame(callback) {
  document.addEventListener('be:scroll', callback);
  requestFrame();
  return () => document.removeEventListener('be:scroll', callback);
}

const boot = () => scanMotion(document);
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
else boot();

// Theme editor: re-scan sections after they re-render.
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) scanMotion(event.target);
});